import "leaflet/dist/leaflet.css";
import Vue from "vue";
import "../../main.js";
import "../theme/js/main.js";
import auth, { handleAuth } from "./boot/auth";
import {
  installSessionExpiryHandler,
  redirectToLogin
} from "./boot/session";
import initRouter from "./boot/initRouter";
import initStore from "./boot/store/initStore";
import initLocalforage from "./boot/localforage/initLocalforage";
import App from "./components/AppComponent";
import "./boot/directives";
import "./boot/filters";
import initComponents from "./boot/components";
import BootstrapVue from "bootstrap-vue";
import "bootstrap-vue/dist/bootstrap-vue.css";
import VueClipboard from "vue-clipboard2";
import VueSnackbar from "vue-snack";
import "vue-snack/dist/vue-snack.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import * as Sentry from "@sentry/vue";
import { isCrossOriginFrameError } from "./helpers/isCrossOriginFrameError";
import { isGoogleMapsNullListenerError } from "./helpers/isGoogleMapsNullListenerError";

Vue.use(BootstrapVue);
Vue.use(VueClipboard);
Vue.use(VueSnackbar, { position: "bottom", time: 3000 });

Vue.prototype.i18n = i18n;
Vue.prototype.env = env;

if (localStorage.getItem("locale")) {
  axios.defaults.headers.common["X-Locale"] = localStorage.getItem("locale");
}

initComponents(env.GOOGLE_API_KEY_FRONTEND);

// Cookiebot's consent iframe (and similar third-party frames on /welcome)
// throw SecurityError when they read parent/top. Swallow that here so Vue
// does not treat it as an app error; Sentry still drops it below (RR-362).
window.addEventListener(
  "error",
  event => {
    const stack = [event.filename, event.error && event.error.stack]
      .filter(Boolean)
      .join("\n");
    const value = event.error || event.message;
    if (
      isCrossOriginFrameError(value) ||
      isGoogleMapsNullListenerError(value, stack)
    ) {
      event.preventDefault();
    }
  },
  true
);

initLocalforage();
const store = initStore(auth);
const router = initRouter(store);
installSessionExpiryHandler(store, router);

if (!Sentry.getClient()) {
  Sentry.init({
    App,
    dsn: process.env.MIX_SENTRY_VUE_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        ignore: [
          ".leaflet-container",
          ".leaflet-google-mutant",
          "#CybotCookiebotDialog",
          "#CookiebotWidget"
        ]
      })
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: ["localhost", env.API_URL],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /_northEast\.(lat|lng)/,
      /_southWest\.(lat|lng)/,
      /_removePath/,
      /Blocked a frame with origin/,
      /from accessing a cross-origin (?:frame|object)/,
      /Permission denied to access property .+ on cross-origin/,
      /Failed to read a named property .+ from 'Window'/
    ],
    denyUrls: [
      /consentcdn\.cookiebot\.com/i,
      /consent\.cookiebot\.com/i
    ],
    beforeSend(event, hint) {
      const exception = hint?.originalException || hint?.syntheticException;
      const value = event?.exception?.values?.[0]?.value;
      const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
      const stack = frames
        .map(frame => frame.filename || frame.abs_path || "")
        .join("\n");

      if (isCrossOriginFrameError(exception) || isCrossOriginFrameError(value)) {
        return null;
      }

      // RENTRUNNER-FRONTEND-HV/HS/HW: Maps iframe + Cookiebot/Rocket Loader (home + return-locations)
      if (
        isGoogleMapsNullListenerError(exception, stack) ||
        isGoogleMapsNullListenerError(value, stack)
      ) {
        return null;
      }

      return event;
    }
  });
}

export default new Vue({
  store,
  el: "#app",
  router: router,
  mounted() {
    // handleAuth rejects with a notices.login_reasons key when the session is
    // provably gone, and with null when the bootstrap merely failed (RR-330).
    handleAuth(store).catch(reason => {
      if (this._isDestroyed || this._isBeingDestroyed) return;

      redirectToLogin(this.$router, reason);
    });
  },
  render: h => h(App)
});
